import type { Account, DashboardData, Reconciliation, Transaction } from "../types";
import { roundToCents } from "./money-math";
import { accountMovementMinor } from "./account-balances";

const monthEnd = (month: string) => `${month}-${String(new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate()).padStart(2, "0")}`;

const previousMonth = (month: string) => {
  const date = new Date(`${month}-01T12:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() - 1);
  return date.toISOString().slice(0, 7);
};

export function reconciliationMonth(today: string) {
  const month = today.slice(0, 7);
  if (today === monthEnd(month)) return month;
  return previousMonth(month);
}

export function closingBalance(account: Account, transactions: Transaction[], month: string) {
  const end = monthEnd(month);
  let balance = roundToCents(account.openingBalance ?? account.balance);
  for (const transaction of transactions) {
    if (transaction.date.slice(0, 10) <= end) balance += accountMovementMinor(account, transaction);
  }
  return balance / 100;
}

function hash(value: string) {
  let result = 2166136261;
  for (let i = 0; i < value.length; i++) {
    result ^= value.charCodeAt(i);
    result = Math.imul(result, 16777619);
  }
  return (result >>> 0).toString(36);
}

// `localeCompare` with no arguments, built once rather than per comparison.
const collator = new Intl.Collator();

export function ledgerSignature(account: Account, transactions: Transaction[], month: string) {
  const end = monthEnd(month);
  // Each row is serialised once and sorted by that key. Serialising inside the comparator did it
  // twice per comparison — O(n log n) stringifies over every transaction the account has.
  const rows = transactions
    .filter((transaction) => transaction.date.slice(0, 10) <= end && (transaction.accountId === account.id || transaction.toAccountId === account.id))
    .map((transaction) => {
      const row = [transaction.id, transaction.date, transaction.type, transaction.accountId, transaction.toAccountId || "", transaction.amount, transaction.receivedAmount || 0, transaction.fee || 0];
      return { row, key: JSON.stringify(row) };
    })
    .sort((a, b) => collator.compare(a.key, b.key))
    .map(({ row }) => row);
  return hash(JSON.stringify([account.id, account.openingBalance ?? account.balance, rows]));
}

export function latestReconciliations(rows: Reconciliation[], month: string) {
  const latest = new Map<string, Reconciliation>();
  for (const row of rows.filter((item) => item.month === month).sort((a, b) => a.confirmedAt.localeCompare(b.confirmedAt))) latest.set(row.accountId, row);
  return latest;
}

function snapshotState(data: DashboardData, month: string) {
  const snapshot = data.coverage?.snapshot;
  // Everything but the due month needs the whole ledger; callers wait for it before asking.
  if (snapshot?.month !== month) throw new Error(`The balance review for ${month} needs the full ledger.`);
  const items = snapshot.items.flatMap((item) => {
    const account = (data.accounts || []).find((a) => a.id === item.accountId && a.active !== false);
    return account ? [{ account, expected: item.expected, signature: item.signature, saved: item.saved, valid: item.valid }] : [];
  });
  const completion = snapshot.completion;
  return { items, completion, complete: !!completion && items.every((item) => item.valid) };
}

export function reconciliationState(data: DashboardData, month: string) {
  if (data.coverage) return snapshotState(data, month);
  const accounts = (data.accounts || []).filter((account) => account.active !== false);
  const transactions = data.transactions || [];
  const latest = latestReconciliations(data.reconciliations || [], month);
  const items = accounts.map((account) => {
    const expected = closingBalance(account, transactions, month);
    const signature = ledgerSignature(account, transactions, month);
    const saved = latest.get(account.id);
    const valid = !!saved && saved.ledgerSignature === signature && Math.abs(saved.expectedBalance - expected) < 0.005;
    return { account, expected, signature, saved, valid };
  });
  const completion = [...(data.reconciliationCompletions || [])].reverse().find((item) => item.month === month);
  return { items, completion, complete: !!completion && items.every((item) => item.valid) };
}

export function pendingThrough(data: DashboardData, month: string) {
  return (data.transactions || []).filter((transaction) => transaction.pending && transaction.date.slice(0, 7) <= month);
}

export function monthHasActivity(data: DashboardData, month: string) {
  if (data.coverage) return data.coverage.months.includes(month);
  return (data.transactions || []).some((transaction) => transaction.date.slice(0, 7) === month);
}

// The month-end nag targets whichever month most recently had real activity, not simply "last
// calendar month" — so a household that hasn't started yet, or that paused for a while, is never
// asked to reconcile empty months it was never using Barnyard for. Older skipped months (activity
// that was never reconciled before the household moved on) are left alone too, the same way a
// missed month already works today: no badge, no block, no forced catch-up.
export function reconciliationDueMonth(data: DashboardData, today: string) {
  // Worked out by the server from the whole ledger when only part of it is loaded.
  if (data.coverage) return data.coverage.dueMonth;
  let month = reconciliationMonth(today);
  for (let i = 0; i < 240; i++) {
    if (monthHasActivity(data, month))
      return reconciliationState(data, month).complete ? "" : month;
    month = previousMonth(month);
  }
  return "";
}

export function isReconciledMonth(data: DashboardData, month: string) {
  if (!/^20\d{2}-\d{2}$/.test(month)) return false;
  if (data.coverage) return data.coverage.reconciledMonths.includes(month);
  return reconciliationState(data, month).complete;
}

export function reconciledMonths(data: DashboardData, dates: (string | undefined)[]) {
  const first = dates.filter((date): date is string => !!date && /^\d{4}-\d{2}/.test(date))
    .map(date => date.slice(0, 7)).sort()[0];
  if (!first) return [];
  // A backdated change carries into later confirmed months, even when the source
  // month itself was never reconciled.
  const completed = data.coverage?.reconciledMonths ?? (data.reconciliationCompletions || []).map(row => row.month);
  return [...new Set(completed)].filter(month => month >= first && isReconciledMonth(data, month)).sort();
}

export const reconciliationMonthLabel = (month: string) =>
  new Date(`${month}-01T12:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
